import {
  repoMap,
  frameworks,
  compareView,
  MANN_WHITNEY_U,
  DEFAULT_FRAMEWORK_ID,
} from '../../common/constants';
import {
  fetchFakeCompareResults,
  memoizedFetchCompareResults,
  memoizedFetchRevisionForRepository,
} from '../../logic/treeherder';
import {
  Changeset,
  CombinedResultsItemType,
  Repository,
} from '../../types/state';
import { FakeCommitHash, Framework, TestVersion } from '../../types/types';

// Parses the raw `framework` URL values into a validated, de-duplicated list
// of framework ids. When no value is provided, it defaults to the default
// framework (talos) so that manually typing the URL is easier.
export function parseFrameworkIds(
  frameworkValues: string[],
): Framework['id'][] {
  if (!frameworkValues.length) {
    return [DEFAULT_FRAMEWORK_ID];
  }

  const frameworkIds = [
    ...new Set(
      frameworkValues.map((value) => {
        const frameworkId = +value as Framework['id'];
        if (Number.isNaN(frameworkId)) {
          throw new Error(
            `The parameter framework should be a number, but it is "${value}".`,
          );
        }
        return frameworkId;
      }),
    ),
  ];

  for (const frameworkId of frameworkIds) {
    if (!frameworks.some((entry) => entry.id === frameworkId)) {
      throw new Error(
        `The parameter framework isn't a valid value: "${frameworkId}".`,
      );
    }
  }

  return frameworkIds;
}

// This function checks and sanitizes the input values, then returns values that
// we can then use in the rest of the application.
export function checkValues({
  baseRev,
  baseRepo,
  newRevs,
  newRepos,
  frameworkValues,
  replicates,
  testVersion,
}: {
  baseRev: string | null;
  baseRepo: Repository['name'] | null;
  newRevs: string[];
  newRepos: Repository['name'][];
  frameworkValues: string[];
  replicates: boolean;
  testVersion: TestVersion | null;
}): {
  baseRev: string;
  baseRepo: Repository['name'];
  newRevs: string[];
  newRepos: Repository['name'][];
  frameworkIds: Framework['id'][];
  replicates: boolean;
  testVersion: TestVersion;
} {
  if (baseRev === null) {
    throw new Error('The parameter baseRev is missing.');
  }

  if (baseRepo === null) {
    throw new Error('The parameter baseRepo is missing.');
  }

  const validRepoValues = Object.values(repoMap);
  if (!validRepoValues.includes(baseRepo)) {
    throw new Error(
      `The parameter baseRepo "${baseRepo}" should be one of ${validRepoValues.join(
        ', ',
      )}.`,
    );
  }

  const frameworkIds = parseFrameworkIds(frameworkValues);

  if (testVersion === MANN_WHITNEY_U || testVersion === null) {
    replicates = true;
  }

  if (testVersion === null) {
    testVersion = MANN_WHITNEY_U;
  }

  if (!newRevs.length) {
    return {
      baseRev,
      baseRepo,
      newRevs: [baseRev],
      newRepos: [baseRepo],
      frameworkIds,
      replicates,
      testVersion,
    };
  }

  if (newRevs.length !== newRepos.length) {
    throw new Error(
      'There should be as many "newRepo" parameters as there are "newRev" parameters.',
    );
  }
  if (!newRepos.every((newRepo) => validRepoValues.includes(newRepo))) {
    throw new Error(
      `Every parameter newRepo "${newRepos.join(
        '", "',
      )}" should be one of ${validRepoValues.join(', ')}.`,
    );
  }

  return {
    baseRev,
    baseRepo,
    newRevs,
    newRepos,
    frameworkIds,
    replicates,
    testVersion,
  };
}

// This is essentially a glue to call the related function from
// /logic/treeherder.ts for all the revs we need results for. Each framework
// is requested separately so that the server doesn't have to handle one giant
// request for all frameworks at once. The results of all frameworks are then
// merged per revision. Failures are tracked per framework so that the results
// of the frameworks that did load can still be displayed.
async function fetchCompareResultsOnTreeherder({
  baseRev,
  baseRepo,
  newRevs,
  newRepos,
  frameworkIds,
  replicates,
  testVersion,
}: {
  baseRev: string;
  baseRepo: Repository['name'];
  newRevs: string[];
  newRepos: Repository['name'][];
  frameworkIds: Framework['id'][];
  replicates: boolean;
  testVersion?: TestVersion;
}): Promise<{
  results: CombinedResultsItemType[][];
  failedFrameworkIds: Framework['id'][];
}> {
  const settledResults = await Promise.allSettled(
    frameworkIds.map((framework) =>
      Promise.all(
        newRevs.map((newRev, i) =>
          memoizedFetchCompareResults({
            baseRev,
            baseRepo,
            newRev,
            newRepo: newRepos[i],
            framework,
            replicates,
            testVersion,
          }),
        ),
      ),
    ),
  );

  const results = newRevs.map(() => [] as CombinedResultsItemType[]);
  const failedFrameworkIds: Framework['id'][] = [];

  settledResults.forEach((settled, index) => {
    if (settled.status === 'fulfilled') {
      settled.value.forEach((revResults, revIndex) => {
        results[revIndex].push(...revResults);
      });
    } else {
      failedFrameworkIds.push(frameworkIds[index]);
    }
  });

  return { results, failedFrameworkIds };
}

const fakeCommitHashes: FakeCommitHash[] = [
  'bb6a5e451dace3b9c7be42d24c9272738d73e6db',
  '9d50665254899d8431813bdc04178e6006ce6d59',
  'a998c42399a8fcea623690bf65bef49de20535b4',
];

// This is essentially a glue to call the related function from
// /logic/treeherder.ts for all the revs we need results for.
async function fetchAllFakeCompareResults() {
  const promises = fakeCommitHashes.map((hash) =>
    fetchFakeCompareResults(hash),
  );
  return Promise.all(promises);
}

// This counter is incremented for each call of the loader. This allows the
// components to know when a new load happened and use it in keys.
// Essentially a workaround to https://github.com/remix-run/react-router/issues/11864
let generationCounter = 0;

// This function is responsible for fetching the data from the URL. It's called
// by React Router DOM when the compare-results path is requested.
// It uses the URL parameters as inputs, and returns all the fetched data to the
// React components through React Router's useLoaderData hook.
export async function loader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const useFakeData = url.searchParams.has('fakedata');
  if (useFakeData) {
    const results = await fetchAllFakeCompareResults();
    // They're all based on the same rev
    const baseRev = results[0][0].base_rev;
    // And the same repository
    const baseRepo = results[0][0].base_repository_name;

    const newRevs = fakeCommitHashes;
    const newRepos = results.map((result) => result[0].new_repository_name);
    const frameworkId = results[0][0].framework_id;

    return {
      results,
      failedFrameworks: Promise.resolve([]),
      baseRev,
      baseRepo,
      newRevs,
      newRepos,
      frameworkIds: [frameworkId],
      view: compareView,
      generation: generationCounter++,
    };
  }

  const baseRevFromUrl = url.searchParams.get('baseRev');
  const baseRepoFromUrl = url.searchParams.get('baseRepo') as
    | Repository['name']
    | null;
  const newRevsFromUrl = url.searchParams.getAll('newRev');
  const newReposFromUrl = url.searchParams.getAll(
    'newRepo',
  ) as Repository['name'][];
  const frameworkFromUrl = url.searchParams.getAll('framework');
  const replicatesFromUrl = url.searchParams.has('replicates');
  const testVersionFromUrl = url.searchParams.get(
    'test_version',
  ) as TestVersion;

  const {
    baseRev,
    baseRepo,
    newRevs,
    newRepos,
    frameworkIds,
    replicates,
    testVersion,
  } = checkValues({
    baseRev: baseRevFromUrl,
    baseRepo: baseRepoFromUrl,
    newRevs: newRevsFromUrl,
    newRepos: newReposFromUrl,
    frameworkValues: frameworkFromUrl,
    replicates: replicatesFromUrl,
    testVersion: testVersionFromUrl,
  });

  return await getComparisonInformation(
    baseRev,
    baseRepo,
    newRevs,
    newRepos,
    frameworkIds,
    replicates,
    testVersion,
  );
}

export async function getComparisonInformation(
  baseRev: string,
  baseRepo: Repository['name'],
  newRevs: string[],
  newRepos: Repository['name'][],
  frameworkIds: Framework['id'][],
  replicates: boolean,
  testVersion?: TestVersion,
) {
  const fetchPromise = fetchCompareResultsOnTreeherder({
    baseRev,
    baseRepo,
    newRevs,
    newRepos,
    frameworkIds,
    replicates,
    testVersion,
  });

  const resultsPromise = fetchPromise.then(({ results }) => results);
  const failedFrameworksPromise = fetchPromise.then(({ failedFrameworkIds }) =>
    failedFrameworkIds
      .map(
        (frameworkId) =>
          frameworks.find((framework) => framework.id === frameworkId)?.name,
      )
      .filter((name): name is Framework['name'] => name !== undefined),
  );

  // TODO what happens if there's no result?
  const baseRevInfoPromise = memoizedFetchRevisionForRepository({
    repository: baseRepo,
    hash: baseRev,
  });
  const newRevsInfoPromises = newRevs.map((newRev, i) =>
    memoizedFetchRevisionForRepository({
      repository: newRepos[i],
      hash: newRev,
    }),
  );

  const [baseRevInfo, ...newRevsInfo] = await Promise.all([
    baseRevInfoPromise,
    ...newRevsInfoPromises,
  ]);

  return {
    results: resultsPromise,
    failedFrameworks: failedFrameworksPromise,
    baseRev,
    baseRevInfo,
    baseRepo,
    newRevs,
    newRevsInfo,
    newRepos,
    frameworkIds,
    view: compareView,
    generation: generationCounter++,
    replicates,
    testVersion,
  };
}

type DeferredLoaderData = {
  results: Promise<CombinedResultsItemType[][]>;
  failedFrameworks: Promise<Framework['name'][]>;
  baseRev: string;
  baseRevInfo: Changeset;
  baseRepo: Repository['name'];
  newRevs: string[];
  newRevsInfo: Changeset[];
  newRepos: Repository['name'][];
  frameworkIds: Framework['id'][];
  view: typeof compareView;
  generation: number;
  replicates: boolean;
  testVersion: TestVersion;
};

// Be explicit with the returned type to control it better than if we were
// inferring it.
export type LoaderReturnValue = DeferredLoaderData;
